// src/layout/customer/ProjectLive/LiveCard.jsx
import React, { useEffect, useState } from "react"; // Added useState
import { useNavigate } from "react-router-dom";
import {
  Building2,
  ClipboardList,
  MapPin,
  CalendarDays,
  IndianRupee,
  Activity,
  MessageCircle,
  CreditCard,
  FileText,
  Loader2,
  UserCircle,
  BadgeCheck,
  Phone,
  X, // For modal close
  AlertTriangle, // For modal error
  Eye, // For modal view link
  ArrowUpRightIcon, // For modal view link
  ExternalLink, // Added for View Details
} from "lucide-react";
import useProjectStatus from "../../../store/builders/useProjectStatus"; // Shared store
import { motion, AnimatePresence } from "framer-motion"; // For modal
import { toast } from "react-hot-toast"; // For notifications
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import WeeklyUpdateSidebarView from "../../builders/ERP/WeeklyUpdateSidebarView";

// ... (helper functions remain the same)
// Helper functions (capitalize, formatCurrency, formatDate) remain the same
const capitalize = (s) => (s && s[0].toUpperCase() + s.slice(1)) || "";
const formatCurrency = (amount) => {
  if (amount == null || isNaN(Number(amount))) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "Invalid Date";
  }
};

// Helper to get file icon (same as in ProjectTable)
const getFileIcon = (fileName) => {
  if (!fileName)
    return <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />;
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(extension))
    return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension))
    return <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  return <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />;
};

const LiveCard = () => {
  const {
    projects: liveProjects,
    isLoading,
    fetchProjects,
    checkCompletionDocumentsStatus, // <-- Get this from the store
    error,
  } = useProjectStatus();
  const navigate = useNavigate();

  // --- NEW: State for Document Modal ---
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [selectedProjectForDocs, setSelectedProjectForDocs] = useState(null);
  const [docsModalData, setDocsModalData] = useState(null);
  const [isFetchingDocs, setIsFetchingDocs] = useState(false);
  const [docsModalError, setDocsModalError] = useState(null);
  // --- END NEW STATE ---

  const [isWeeklyModalOpen, setIsWeeklyModalOpen] = useState(false);
  const [selectedProjectForWeekly, setSelectedProjectForWeekly] = useState(null);
  const [weeklyUpdates, setWeeklyUpdates] = useState([]);
  const [selectedWeeklyUpdateId, setSelectedWeeklyUpdateId] = useState(null);
  const [isFetchingWeekly, setIsFetchingWeekly] = useState(false);
  const [weeklyModalError, setWeeklyModalError] = useState(null);

  useEffect(() => {
    fetchProjects("active");
  }, [fetchProjects]);

  const handleNavigate = (path, projectId = null) => {
    if (projectId && (path.includes("/payment") || path.includes("/chat"))) {
      let dynamicPath = path;
      if (path.includes("/payment")) {
        dynamicPath = `/customer/project/${projectId}/payment`;
      } else if (path.includes("/chat")) {
        dynamicPath = `/customer/project/${projectId}/chat`;
      }
      navigate(dynamicPath);
    } else {
      navigate(path);
    }
  };

  // --- NEW: Functions for Document Modal ---
  const fetchDocsForModal = async (projectId) => {
    setIsFetchingDocs(true);
    setDocsModalError(null);
    setDocsModalData(null);
    try {
      const result = await checkCompletionDocumentsStatus(projectId);
      if (result && result.success !== false) {
        // Check if success is explicitly false
        setDocsModalData(result);
      } else {
        setDocsModalError(
          result?.message ||
          "Could not fetch document status. Please try again later."
        );
        // toast.error(result?.message || "Could not fetch document status."); // Optional: Toast error
      }
    } catch (error) {
      setDocsModalError("An error occurred while fetching document status.");
      console.error("Docs Fetch Error in LiveCard:", error);
      // toast.error("Error fetching documents."); // Optional: Toast error
    } finally {
      setIsFetchingDocs(false);
    }
  };

  const handleViewDocuments = (project) => {
    setSelectedProjectForDocs(project);
    setIsDocsModalOpen(true);
    fetchDocsForModal(project.id);
  };

  const closeDocsModal = () => {
    setIsDocsModalOpen(false);
    setSelectedProjectForDocs(null);
    setDocsModalData(null);
    setDocsModalError(null);
  };

  const fetchWeeklyUpdatesForModal = async (projectId) => {
    setIsFetchingWeekly(true);
    setWeeklyModalError(null);
    setWeeklyUpdates([]);
    setSelectedWeeklyUpdateId(null);
    try {
      const res = await axiosInstancev1.get(
        `/erp/weekly-updates/customer/project/${projectId}`
      );
      const rows = (res.data?.data || []).filter((u) => u.status === "submitted");
      setWeeklyUpdates(rows);
      if (rows.length > 0) setSelectedWeeklyUpdateId(rows[0].id);
    } catch (error) {
      const msg = error.response?.data?.message || "Could not load weekly updates";
      setWeeklyModalError(msg);
      toast.error(msg);
    } finally {
      setIsFetchingWeekly(false);
    }
  };

  const handleViewWeeklyUpdates = (project) => {
    setSelectedProjectForWeekly(project);
    setIsWeeklyModalOpen(true);
    fetchWeeklyUpdatesForModal(project.id);
  };

  const closeWeeklyModal = () => {
    setIsWeeklyModalOpen(false);
    setSelectedProjectForWeekly(null);
    setWeeklyUpdates([]);
    setSelectedWeeklyUpdateId(null);
    setWeeklyModalError(null);
  };
  // --- END NEW FUNCTIONS ---

  if (error) {
    return <p className="text-center text-red-500 p-4">{error}</p>;
  }

  if (isLoading && liveProjects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isLoading && liveProjects.length === 0) {
    return (
      <div className="text-center text-gray-600 p-8 text-sm min-h-[60vh] flex flex-col justify-center items-center">
        <img
          src="/assets/check.png"
          alt="No Live Projects"
          className="w-20 h-auto mb-4 opacity-70"
        />
        <p className="text-lg font-semibold">No Live Projects</p>
        <p>You do not have any projects currently in progress.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {liveProjects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex flex-col transition-all duration-300 hover:shadow-xl h-full"
          >
            {/* Builder Info */}
            <div className="mb-3 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-3 mb-1.5 min-w-0">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden border border-blue-200 flex-shrink-0">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3
                    className="text-sm sm:text-base font-semibold text-gray-800 truncate sm:whitespace-normal sm:break-words"
                    title={project.builder_company}
                  >
                    {project.builder_company || "N/A"}
                  </h3>
                  {project.builder_name && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <UserCircle className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{project.builder_name}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2 text-gray-600">
                  <BadgeCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span>
                    Ducktail ID: {project.builder_ducktail_id || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    {project.builder_contact
                      ? `+91 ${project.builder_contact}`
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <h4
                  className="text-xs font-medium text-gray-700 truncate"
                  title={project.project_name}
                >
                  {project.project_name || "Project Name N/A"}
                </h4>
              </div>
              <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate" title={project.project_location}>
                    {project.project_location || "Location N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{formatCurrency(project.project_budget)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Start</p>
                      <p className="font-medium">
                        {formatDate(project.starting_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Deadline</p>
                      <p className="font-medium">
                        {formatDate(project.deadline_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="flex items-center gap-1.5 text-xs">
                  <Activity className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-500">Status:</span>
                  <span
                    className={`font-semibold capitalize px-1.5 py-0.5 rounded-full text-white text-[9px] ${project.status === "active"
                        ? "bg-green-500"
                        : "bg-gray-500"
                      }`}
                  >
                    {capitalize(project.status) || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                onClick={() =>
                  handleNavigate(`/customer/project/${project.id}/payment`)
                }
                className="flex-1 px-2 py-1.5 bg-blue-50 text-blue-600 rounded-md text-[10px] font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                title="View Payment History"
              >
                <CreditCard className="w-3 h-3" /> Payment
              </button>
              <button
                onClick={() => handleViewDocuments(project)}
                className="flex-1 px-2 py-1.5 bg-purple-50 text-purple-600 rounded-md text-[10px] font-medium hover:bg-purple-100 transition-colors flex items-center justify-center gap-1"
                title="View Documents"
              >
                <FileText className="w-3 h-3" /> Documents
              </button>
              <button
                onClick={() => handleViewWeeklyUpdates(project)}
                className="flex-1 px-2 py-1.5 bg-emerald-50 text-emerald-700 rounded-md text-[10px] font-medium hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1"
                title="View Weekly Site Updates"
              >
                <CalendarDays className="w-3 h-3" /> Weekly
              </button>
              <button
                onClick={() => navigate(`/master-data/${project.id}`)}
                className="flex-1 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md text-[10px] font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                title="View All Project Details"
              >
                <ExternalLink className="w-3 h-3" /> Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* --- NEW: Document Status Modal --- */}
      <AnimatePresence>
        {isDocsModalOpen && selectedProjectForDocs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={closeDocsModal} // Click overlay to close
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 w-full max-w-lg relative flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()} // Prevent closing on inner click
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Project Documents
                </h3>
                <button
                  onClick={closeDocsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close documents modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                Project: {selectedProjectForDocs.project_name || "N/A"}
              </div>
              <div className="flex-grow overflow-y-auto pr-2 -mr-2 custom-scrollbar">
                {isFetchingDocs ? (
                  <div className="flex justify-center items-center h-40">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500 text-sm">
                      Loading documents...
                    </span>
                  </div>
                ) : docsModalError ? (
                  <div className="text-center py-6 px-4 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm flex flex-col items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />{" "}
                    {docsModalError}
                  </div>
                ) : docsModalData?.documents &&
                  docsModalData.documents.length > 0 ? (
                  <div className="space-y-2.5">
                    {docsModalData.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-xs"
                      >
                        <div className="flex items-center gap-2 flex-grow mr-2">
                          {getFileIcon(doc.url)}
                          <span className="font-medium text-gray-700">
                            {doc.name}
                          </span>
                          {/* For customer view, "Required" might be less relevant, but shown for consistency */}
                          {doc.required && (
                            <span className="text-gray-400 text-[9px] italic">
                              (Required by builder)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {doc.uploaded ? (
                            <span className="text-green-600 font-semibold text-[9px] bg-green-100 px-1.5 py-0.5 rounded-full">
                              Provided
                            </span>
                          ) : (
                            <span className="text-orange-600 font-semibold text-[9px] bg-orange-100 px-1.5 py-0.5 rounded-full">
                              Awaited
                            </span>
                          )}
                          {doc.uploaded && doc.url ? (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-[10px] font-medium flex items-center gap-0.5"
                              title={`View ${doc.name}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </a>
                          ) : (
                            <span className="text-gray-400 text-[10px] italic">
                              Not Available
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-6 text-sm">
                    No document information available for this project from the
                    builder.
                  </p>
                )}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={closeDocsModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* --- END Document Modal --- */}

      {/* Weekly Update Modal */}
      <AnimatePresence>
        {isWeeklyModalOpen && selectedProjectForWeekly && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={closeWeeklyModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 w-full max-w-4xl relative flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-emerald-600" />
                  Weekly Site Updates
                </h3>
                <button
                  onClick={closeWeeklyModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close weekly updates"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                {selectedProjectForWeekly.project_name || "Project"} — photos and progress from your builder
              </p>
              <div className="flex-grow overflow-y-auto min-h-[200px]">
                {isFetchingWeekly ? (
                  <div className="flex justify-center items-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                    <span className="ml-2 text-gray-500 text-sm">Loading weekly updates...</span>
                  </div>
                ) : weeklyModalError ? (
                  <div className="text-center py-8 px-4 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm flex flex-col items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    {weeklyModalError}
                  </div>
                ) : weeklyUpdates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 text-sm">
                    <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    No weekly updates submitted for this project yet.
                  </div>
                ) : (
                  <WeeklyUpdateSidebarView
                    updates={weeklyUpdates}
                    selectedUpdateId={selectedWeeklyUpdateId}
                    onSelectUpdate={setSelectedWeeklyUpdateId}
                    accent="emerald"
                    emptyMessage="No weekly updates submitted yet."
                  />
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={closeWeeklyModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default LiveCard;
